import { useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import QRCode from "qrcode";
import { Printer as PrinterIcon, Loader2, X, XCircle, MoreVertical, CheckCircle2, DollarSign, ChevronsRight, Search, Filter, FileText, Clock, RefreshCw, Eye, Pencil, Save, ChevronLeft, ChevronRight, ChevronDown, AlertTriangle } from "lucide-react";
import "../styles/admin-base.css";
import "../styles/id-card-management.css";

import client, { BASE_URL } from "../../src/api/client";

import { getUnionBranding } from "../../src/utils/unionBranding";

// Minimal digital ID styling to render fetched card markup like the user-facing PDF.
const DIGITAL_ID_CSS = `
.digital-id-wrapper { display: flex; justify-content: center; align-items: center; padding: 16px; }
.digital-id-card { perspective: 1600px; display: flex; justify-content: center; padding: 0 12px; }
.digital-id-card__inner { position: relative; width: min(380px, 100%); aspect-ratio: 2 / 3.35; min-height: 540px; transform-style: preserve-3d; }
.digital-id-card__face { position: absolute; inset: 0; backface-visibility: hidden; background: #fff; border-radius: 28px; overflow: hidden; box-shadow: 0 40px 80px rgba(15,23,42,0.15); border: 1px solid rgba(15,23,42,0.1); display: flex; flex-direction: column; }
.digital-id-card__face--back { transform: rotateY(180deg); }
.digital-id-card__header { display: flex; gap: 16px; align-items: center; padding: 24px 22px 20px; background: linear-gradient(135deg, #dc2626, #b91c1c); color: #f8fafc; position: relative; }
.digital-id-card__header::before { content: ""; display: none; }
.digital-id-card__header::after { content: ""; position: absolute; inset: 0; background-image: radial-gradient(circle at 20% 20%, rgba(248, 250, 252, 0.12) 0%, transparent 55%); pointer-events: none; }
.digital-id-card__header p { margin: 0; font-size: 1rem; font-weight: 700; letter-spacing: 0.05em; }
.digital-id-card__header span { display: block; margin-top: 6px; font-size: 0.82rem; color: rgba(248, 250, 252, 0.8); }
.digital-id-card__seal { width: 90px; height: 90px; border-radius: 26px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
.digital-id-card__seal img { width: 76px; height: 76px; max-width: 76px; max-height: 76px; object-fit: contain; object-position: center; display: block; }
.digital-id-card__member-banner { text-align: center; background: linear-gradient(135deg, #1e3a8a, #1d4ed8); color: #f8fafc; letter-spacing: 0.32em; padding: 12px 0; font-weight: 700; font-size: 0.82rem; }
.digital-id-card__body { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 18px; padding: 24px 24px 18px; background: linear-gradient(180deg, rgba(15, 23, 42, 0.04) 0%, rgba(15, 23, 42, 0.01) 100%); position: relative; }
.digital-id-card__body::before { content: ""; display: none; }
.digital-id-card__photo { background: #f8fafc; border: 2px solid rgba(15, 23, 42, 0.12); border-radius: 16px; width: 164px; height: 164px; display: grid; place-items: center; overflow: hidden; position: relative; }
.digital-id-card__photo img { width: 100%; height: 100%; object-fit: cover; }
.digital-id-card__initials { width: 84px; height: 84px; border-radius: 50%; background: linear-gradient(135deg, #2563eb, #4f46e5); color: #f8fafc; display: grid; place-items: center; font-size: 1.4rem; font-weight: 700; }
.digital-id-card__info { display: flex; flex-direction: column; gap: 8px; align-items: center; text-align: center; width: 100%; }
.digital-id-card__meta { display: flex; flex-direction: column; align-items: center; gap: 6px; width: 100%; font-size: 0.85rem; color: #475569; }
.digital-id-card__status { display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 999px; background: rgba(22, 163, 74, 0.12); color: #16a34a; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; font-size: 0.75rem; }
.digital-id-card__signature { margin-top: auto; padding-top: 14px; text-align: center; font-size: 0.85rem; font-weight: 600; color: rgba(15, 23, 42, 0.7); }
.digital-id-card__header--back { background: linear-gradient(135deg, #c2410c, #b91c1c); }
.digital-id-card__qr { flex: 1; padding: 28px 24px 22px; display: flex; flex-direction: column; gap: 18px; align-items: center; justify-content: center; }
.digital-id-card__qr-box { padding: 16px; border-radius: 18px; border: 2px solid rgba(15, 23, 42, 0.12); background: linear-gradient(135deg, rgba(15, 23, 42, 0.02), rgba(15, 23, 42, 0.08)); display: grid; place-items: center; }
.digital-id-card__qr-box img, .digital-id-card__qr-box svg { width: 150px; height: 150px; object-fit: contain; }
.digital-id-card__qr-info { text-align: center; display: flex; flex-direction: column; gap: 6px; color: #1e293b; }
.digital-id-card__qr-info span { font-family: 'IBM Plex Mono', monospace; font-size: 0.95rem; }
.digital-id-card__qr-info small { color: #475569; font-size: 0.8rem; }
.digital-id-card__emergency-contact { text-align: center; display: flex; flex-direction: column; gap: 3px; margin-top: 4px; color: #334155; }
.digital-id-card__emergency-contact strong { font-size: 0.74rem; letter-spacing: 0.02em; }
.digital-id-card__emergency-contact span { font-size: 0.72rem; line-height: 1.25; font-family: 'Segoe UI', Arial, Helvetica, sans-serif; }
.digital-id-card__footer { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 18px 22px; background: linear-gradient(135deg, #1e3a8a, #1d4ed8); color: #f8fafc; font-size: 0.80rem; letter-spacing: 0.05em; }
`;

const formatLabel = (value, fallback = "\u2014") => {
  if (!value) return fallback;
  return value
    .toString()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
};

const formatRequestDate = (value) => {
  if (!value) return "\u2014";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const formatted = date.toLocaleString("en-US", {
    month: "2-digit",
    day: "numeric",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  return formatted.replace(",", " -");
};

const formatTimelineDate = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

const formatPeso = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 'N/A';
  return `PHP ${amount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const coalesceString = (...values) => {
  for (const v of values) {
    if (v === undefined || v === null) continue;
    const str = v.toString().trim();
    if (str) return str;
  }
  return "";
};

const generateQrDataUrl = async (value) => {
  if (!value) return null;
  try {
    return await QRCode.toDataURL(value.toString(), { margin: 1, width: 256 });
  } catch (err) {
    console.error("Failed to generate QR data URL", err);
    return null;
  }
};

const resolveAssetUrl = (value) => {
  if (!value) return null;
  const str = value.toString().trim();
  if (!str) return null;
  // Return as-is if already a data URL or full HTTP URL
  if (str.startsWith("data:")) return str;
  if (/^https?:\/\//i.test(str)) return str;
  // Construct full URL from BASE_URL for relative paths
  if (str.startsWith("/")) return `${BASE_URL}${str}`;
  return `${BASE_URL}/${str}`;
};

const fetchAsDataUrl = async (url) => {
  if (!url) return null;
  if (url.startsWith("data:")) return url;
  try {
    const response = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const normalizeRef = (value) => (value ?? "").toString().trim().toLowerCase();

const normalizeText = (value) => (value ?? "").toString().trim().toLowerCase();

const WORKFLOW_FILTERS = [
  { value: "all", label: "All" },
  { value: "printing_ids", label: "Printing IDs" },
  { value: "releasing", label: "Releasing" },
  { value: "pending", label: "Pending" },
  { value: "mark_and_notify", label: "Mark & Notify" },
  { value: "ready_to_pickup", label: "Ready to Pickup" },
  { value: "rejected", label: "Rejected" },
];

const COMPLETED_WORKFLOW_FILTERS = [
  { value: "all", label: "All" },
  { value: "released", label: "Released" },
  { value: "delivered", label: "Delivered" },
  { value: "stripe_delivery", label: "Stripe Delivery" },
  { value: "cash_pickup", label: "Cash Pickup" },
];

const ACTION_MENU_GAP = 8;
const ACTION_MENU_VIEWPORT_PADDING = 12;
const ACTION_MENU_WIDTH = 220;
const ACTION_MENU_MIN_HEIGHT = 120;
const ACTION_MENU_PREFERRED_HEIGHT = 260;

const computeFixedActionMenuPosition = (triggerElement, options = {}) => {
  if (!triggerElement?.getBoundingClientRect) {
    return {
      top: ACTION_MENU_VIEWPORT_PADDING,
      left: ACTION_MENU_VIEWPORT_PADDING,
      maxHeight: ACTION_MENU_PREFERRED_HEIGHT,
    };
  }

  const rect = triggerElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const menuWidth = options.menuWidth ?? ACTION_MENU_WIDTH;
  const gap = options.gap ?? ACTION_MENU_GAP;
  const padding = options.padding ?? ACTION_MENU_VIEWPORT_PADDING;
  const minHeight = options.minHeight ?? ACTION_MENU_MIN_HEIGHT;
  const preferredHeight = options.preferredHeight ?? ACTION_MENU_PREFERRED_HEIGHT;

  const safeLeft = Math.max(
    padding,
    Math.min(rect.right - menuWidth, viewportWidth - menuWidth - padding),
  );
  const spaceBelow = Math.max(0, viewportHeight - rect.bottom - gap - padding);
  const spaceAbove = Math.max(0, rect.top - gap - padding);
  const shouldOpenUp = spaceBelow < minHeight && spaceAbove > spaceBelow;
  const maxHeight = Math.max(minHeight, shouldOpenUp ? spaceAbove : spaceBelow);
  const desiredHeight = Math.min(preferredHeight, maxHeight);
  const safeTop = shouldOpenUp
    ? Math.max(padding, rect.top - gap - desiredHeight)
    : Math.max(padding, Math.min(rect.bottom + gap, viewportHeight - desiredHeight - padding));

  return {
    top: safeTop,
    left: safeLeft,
    maxHeight,
  };
};

export default function IDCardManagement() {
  const [metrics, setMetrics] = useState({
    totalRequests: 0,
    physicalQueued: 0,
    readyForPickup: 0,
  });
  const [queue, setQueue] = useState([]);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [printLoadingId, setPrintLoadingId] = useState(null);
  const [bulkPrintLoading, setBulkPrintLoading] = useState(false);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({ top: 0, left: 0, maxHeight: ACTION_MENU_PREFERRED_HEIGHT });
  const [moveConfirm, setMoveConfirm] = useState({ open: false, item: null });
  const [readyConfirm, setReadyConfirm] = useState({ open: false, item: null });
  const [releaseModal, setReleaseModal] = useState({
    open: false,
    item: null,
    mode: "cash",
    shippingStatus: "preparing",
    trackingNumber: "",
    paymentReference: "",
    paymentNotes: "",
  });
  const [detailsModal, setDetailsModal] = useState({
    open: false,
    item: null,
  });
  const [rejectModal, setRejectModal] = useState({
    open: false,
    item: null,
    reason: '',
  });
  const [waybillModal, setWaybillModal] = useState({
    open: false,
    item: null,
    html: "",
    loading: false,
    error: null,
  });
  const waybillFrameRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [releasedSearchQuery, setReleasedSearchQuery] = useState("");
  const [workflowFilter, setWorkflowFilter] = useState("all");
  const [completedWorkflowFilter, setCompletedWorkflowFilter] = useState("all");
  const [activePage, setActivePage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const [cardPricePhp, setCardPricePhp] = useState(150);
  const [idShippingFeePhp, setIdShippingFeePhp] = useState(50);
  const [isEditingPricing, setIsEditingPricing] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [settingsSuccess, setSettingsSuccess] = useState(null);
  const [systemSettingsSnapshot, setSystemSettingsSnapshot] = useState({});

  const fetchOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await client.get("/api/admin/id-cards/overview");
      const payload = response.data ?? {};
      const metricPayload = payload.metrics ?? {};

      setMetrics({
        totalRequests: Number(metricPayload.totalRequests ?? metricPayload.totalQueued ?? 0),
        physicalQueued: Number(metricPayload.physicalQueued ?? 0),
        readyForPickup: Number(metricPayload.readyForPickup ?? 0),
      });
      setQueue(Array.isArray(payload.queue) ? payload.queue : []);
      setGuides(Array.isArray(payload.guides) ? payload.guides : []);
    } catch (err) {
      setError(err?.response?.data?.message ?? "Unable to load ID card overview");
      setQueue([]);
      setGuides([]);
    } finally {
      setLoading(false);
    }
  };

  const extractCardPrice = (payload) => {
    const settings = payload?.settings || {};
    const nested = settings?.systemSettings;

    const direct = Number(settings?.cardPrice);
    if (Number.isFinite(direct) && direct >= 0) {
      return direct;
    }

    const nestedValue = Number(nested?.cardPrice);
    if (Number.isFinite(nestedValue) && nestedValue >= 0) {
      return nestedValue;
    }

    return 150;
  };

  const extractShippingFee = (payload) => {
    const settings = payload?.settings || {};
    const nested = settings?.systemSettings;

    const direct = Number(settings?.idShippingFee);
    if (Number.isFinite(direct) && direct >= 0) {
      return direct;
    }

    const nestedValue = Number(nested?.idShippingFee);
    if (Number.isFinite(nestedValue) && nestedValue >= 0) {
      return nestedValue;
    }

    return 50;
  };

  const fetchCardPriceSettings = async () => {
    try {
      setSettingsLoading(true);
      setSettingsError(null);
      const response = await client.get('/api/admin/settings/system');
      const payload = response.data || {};
      setSystemSettingsSnapshot(payload?.settings?.systemSettings || {});
      setCardPricePhp(extractCardPrice(payload));
      setIdShippingFeePhp(extractShippingFee(payload));
    } catch (err) {
      setSettingsError(err?.response?.data?.message || 'Unable to load card price settings.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveCardPriceSettings = async () => {
    try {
      setSettingsSaving(true);
      setSettingsError(null);
      setSettingsSuccess(null);

      const normalizedPrice = Number(cardPricePhp);
      if (!Number.isFinite(normalizedPrice) || normalizedPrice < 0) {
        setSettingsError('Card price must be a valid non-negative number.');
        return false;
      }

      const normalizedShippingFee = Number(idShippingFeePhp);
      if (!Number.isFinite(normalizedShippingFee) || normalizedShippingFee < 0) {
        setSettingsError('Shipping fee must be a valid non-negative number.');
        return false;
      }

      const nextSystemSettings = {
        ...systemSettingsSnapshot,
        cardPrice: Number(normalizedPrice.toFixed(2)),
        idShippingFee: Number(normalizedShippingFee.toFixed(2)),
      };

      await client.put('/api/admin/settings/system', {
        settings: {
          systemSettings: nextSystemSettings,
          cardPrice: Number(normalizedPrice.toFixed(2)),
          idShippingFee: Number(normalizedShippingFee.toFixed(2)),
        },
      });

      setSystemSettingsSnapshot(nextSystemSettings);
      setSettingsSuccess('ID card pricing settings saved.');
      return true;
    } catch (err) {
      setSettingsError(err?.response?.data?.message || 'Unable to save card price settings.');
      return false;
    } finally {
      setSettingsSaving(false);
    }
  };

  const handlePricingAction = async () => {
    if (!isEditingPricing) {
      setSettingsError(null);
      setSettingsSuccess(null);
      setIsEditingPricing(true);
      return;
    }

    const saved = await saveCardPriceSettings();
    if (saved) {
      setIsEditingPricing(false);
    }
  };

  const waitForImages = async (container, timeout = 5000) => {
    const images = Array.from(
      container.querySelectorAll ? container.querySelectorAll("img") : []
    );
    if (!images.length) return;
    const loadPromise = Promise.all(
      images.map((img) =>
        img.complete && img.naturalWidth
          ? Promise.resolve()
          : new Promise((resolve) => {
              const done = () => resolve();
              img.addEventListener("load", done, { once: true });
              img.addEventListener("error", done, { once: true });
            }),
      ),
    );
    await Promise.race([
      loadPromise,
      new Promise((resolve) => setTimeout(resolve, timeout)),
    ]);
  };

  const pickCardFaces = (root) => {
    if (!root) return { front: null, back: null };

    const selectors = [
      ".digital-id-card__face--front",
      ".digital-id-card__front",
      "[data-card-front]",
      ".id-card__front",
      ".card-front",
      ".digital-id-card .digital-id-card__face",
      ".digital-id-card__face",
    ];
    const backSelectors = [
      ".digital-id-card__face--back",
      ".digital-id-card__back",
      "[data-card-back]",
      ".id-card__back",
      ".card-back",
      ".digital-id-card .digital-id-card__face:last-child",
    ];

    const queryAll = (node, sel) => (node ? node.querySelector(sel) : null);

    let front = null;
    for (const sel of selectors) {
      front = queryAll(root, sel);
      if (front) break;
    }

    let back = null;
    for (const sel of backSelectors) {
      back = queryAll(root, sel);
      if (back) break;
    }

    if (!front) {
      const faces = root.querySelectorAll ? root.querySelectorAll("*") : [];
      front = faces[0] || root.firstElementChild || null;
      back = faces[1] || root.firstElementChild?.nextElementSibling || front;
    }

    if (!back && front) {
      back = front;
    }

    return { front, back };
  };

  const toCardData = (item) => {
    const metadata = item?.metadata || {};
    const memberName = item?.memberName || metadata.memberName || item?.member || "LWU Member";
    const parts = memberName.split(" ").filter(Boolean);
    const firstName = metadata.firstName || parts[0] || "";
    const lastName = metadata.lastName || parts.slice(1).join(" ") || "";
    const displayName = [firstName, lastName].filter(Boolean).join(" ").toUpperCase() || memberName.toUpperCase();
    const membershipNo = item?.membershipNo || metadata.membershipNo || item?.digitalId || metadata.digitalId || "LWU-000000";
    const shortId = membershipNo.split("-").pop() || membershipNo;
    const company =
      coalesceString(
        metadata.company,
        metadata.companyName,
        metadata.company_name,
        metadata.employer,
        metadata.employerName,
        metadata.employer_name,
        metadata.organization,
        metadata.organizationName,
        metadata.org,
        metadata.orgName,
        metadata.workplace,
        metadata.office,
        metadata.union,
        item?.company,
        item?.companyName,
        item?.company_name,
        item?.employer,
        item?.employerName,
        item?.employer_name,
        item?.organization,
        item?.organizationName,
        item?.org,
        item?.orgName,
        item?.workplace,
        item?.office,
        item?.branch,
      ) || "LWU";
    // Use unionPosition first (e.g., President, Vice President), then position, then default to Member
    const position = metadata.unionPosition || item?.unionPosition || metadata.position || item?.position || metadata.memberPosition || "Member";
    const unionName = metadata.unionName || metadata.union || metadata.unionAffiliation || "LWU";
    // Get union branding (logo, colors, names)
    const branding = getUnionBranding(unionName);
    const qrRef = metadata.qrReference || metadata.qrRef || metadata.qr || shortId;
    const qrValue = metadata.qrValue || metadata.qrData || qrRef || membershipNo || shortId;
    const qrImage =
      metadata.qrCode ||
      metadata.qr_code ||
      metadata.qrImage ||
      metadata.qrUrl ||
      metadata.qr_url ||
      metadata.qr ||
      item?.qrCode ||
      item?.qr_code ||
      item?.qrImage ||
      item?.qrUrl ||
      item?.qr_url ||
      item?.qr ||
      null;
    const photo =
      coalesceString(
        metadata.photo,
        metadata.profilePhoto,
        metadata.profile_picture,
        metadata.avatar,
        metadata.photoUrl,
        metadata.photo_url,
        metadata.image,
        metadata.picture,
        metadata.memberPhoto,
        metadata.member_photo,
        metadata.photoLink,
        item?.photo,
        item?.profilePhoto,
        item?.profile_picture,
        item?.avatar,
        item?.photoUrl,
        item?.photo_url,
        item?.image,
        item?.picture,
        item?.memberPhoto,
        item?.member_photo,
      ) || null;
    const photoResolved = resolveAssetUrl(photo);
    const signatureRaw =
      coalesceString(
        metadata.signature,
        metadata.signatureImage,
        metadata.signatureImg,
        metadata.signatureUrl,
        metadata.signature_url,
        metadata.signatureFile,
        metadata.signatureBase64,
        item?.signature,
        item?.signatureImage,
        item?.signatureImg,
        item?.signatureUrl,
        item?.signature_url,
        item?.signatureFile,
        item?.signatureBase64,
      ) || null;
    const signature = resolveAssetUrl(signatureRaw);
    // Use union branding logo if available, else fallback to logo fields
    const logoRaw = metadata.logo || metadata.logoUrl || metadata.logo_url || metadata.seal || item?.logo || item?.logoUrl || item?.logo_url || null;
    const logo = branding.logo || resolveAssetUrl(logoRaw);
    const contact =
      coalesceString(
        metadata.phone,
        item?.phone,
        metadata.contact,
        metadata.contactNumber,
        metadata.contactNo,
        metadata.contactno,
        metadata.contact_no,
        metadata.contact_number,
        metadata.phoneNumber,
        metadata.phoneno,
        metadata.phone_number,
        metadata.phoneNo,
        metadata.phone_no,
        metadata.mobile,
        metadata.mobileNumber,
        metadata.mobile_number,
        metadata.mobileNo,
        metadata.mobile_no,
        metadata.mobileno,
        metadata.tel,
        metadata.telephone,
        metadata.telephoneNumber,
        metadata.telephone_number,
        item?.contact,
        item?.contactNumber,
        item?.contactNo,
        item?.contactno,
        item?.contact_no,
        item?.contact_number,
        item?.phoneNumber,
        item?.phoneno,
        item?.phone_number,
        item?.phoneNo,
        item?.phone_no,
        item?.mobile,
        item?.mobileNumber,
        item?.mobile_number,
        item?.mobileNo,
        item?.mobile_no,
        item?.mobileno,
        item?.tel,
        item?.telephone,
        item?.telephoneNumber,
        item?.telephone_number,
      ) || "N/A";
    const emergencyContactName =
      coalesceString(
        metadata?.emergencyContact?.name,
        metadata?.emergency_contact?.name,
        metadata.emergencyContactName,
        metadata.emergency_contact_name,
        metadata.emergencyName,
        metadata.emergency_name,
        item?.emergencyContact?.name,
        item?.emergency_contact?.name,
        item?.emergencyContactName,
        item?.emergency_contact_name,
        item?.emergencyName,
        item?.emergency_name,
      ) || "Not Provided";
    const emergencyContactMobile =
      coalesceString(
        metadata?.emergencyContact?.phone,
        metadata?.emergency_contact?.phone,
        metadata?.emergencyContact?.mobile,
        metadata?.emergency_contact?.mobile,
        metadata.emergencyContactPhone,
        metadata.emergency_contact_phone,
        metadata.emergencyContactMobile,
        metadata.emergency_contact_mobile,
        metadata.emergencyMobile,
        metadata.emergency_mobile,
        item?.emergencyContact?.phone,
        item?.emergency_contact?.phone,
        item?.emergencyContactPhone,
        item?.emergency_contact_phone,
        item?.emergencyContactMobile,
        item?.emergency_contact_mobile,
        item?.emergencyMobile,
        item?.emergency_mobile,
      ) || "Not Provided";
    return {
      firstName,
      lastName,
      displayName,
      membershipNo,
      shortId,
      company,
      position,
      unionName,
      qrRef,
      qrValue,
      qrImage,
      photo: photoResolved,
      signature,
      logo,
      contact,
      emergencyContactName,
      emergencyContactMobile,
      branding, // pass branding for use in template
    };
  };

  const buildTemplateHtml = (data) => {
    const branding = data.branding || {};
    const logoSrc = data.logo || branding.logo;
    const qrSrc = data.qrImage || "";
    const initials = `${(data.firstName || "").charAt(0)}${(data.lastName || "").charAt(0)}`.trim().toUpperCase() || (branding.shortName || "LWU");
    const photoBlock = data.photo
      ? `<img crossorigin="anonymous" src="${data.photo}" alt="Member" style="width:100%;height:100%;object-fit:cover;" />`
      : `<div class="digital-id-card__initials" style="width:84px;height:84px;border-radius:50%;background:linear-gradient(135deg,#2563eb,#4f46e5);color:#f8fafc;display:grid;place-items:center;font-size:1.4rem;font-weight:700;">${initials}</div>`;

    // Calculate dynamic font size based on name length (matching Digital ID page logic)
    const nameLength = data.displayName.length;
    let fontSize = '1.5rem';
    if (nameLength > 35) {
      fontSize = '0.75rem';
    } else if (nameLength > 28) {
      fontSize = '0.9rem';
    } else if (nameLength > 22) {
      fontSize = '1.1rem';
    } else if (nameLength > 18) {
      fontSize = '1.25rem';
    } else if (nameLength > 14) {
      fontSize = '1.35rem';
    }

    // Use union-specific names/colors
    const unionFullName = branding.fullName || "LUZON WORKERS UNION";
    const headerGradient = branding.colors?.headerGradient || "linear-gradient(135deg, #dc2626, #b91c1c)";
    const headerBackGradient = branding.colors?.headerBackGradient || "linear-gradient(135deg, #c2410c, #b91c1c)";
    const bannerGradient = branding.colors?.bannerGradient || "linear-gradient(135deg, #1e3a8a, #1d4ed8)";
    const footerGradient = branding.colors?.footerGradient || "linear-gradient(135deg, #1e3a8a, #1d4ed8)";

    return `
      <div class="digital-id-wrapper" style="padding-bottom:24px;">
        <section class="digital-id-card">
          <div class="digital-id-card__inner" style="padding-bottom:16px;">
            <div class="digital-id-card__face digital-id-card__face--front" data-card-front>
              <div class="digital-id-card__header" style="background: ${headerGradient};">
                <div class="digital-id-card__seal"><img src="${logoSrc}" alt="${unionFullName}" /></div>
                <div style="display: flex; flex-direction: column; gap: 2px;">
                  <p style="margin: 0; font-weight: 700; font-size: 1rem; line-height: 1.18; font-family: 'Segoe UI', Arial, Helvetica, sans-serif; letter-spacing: normal; color: #fff; text-transform: uppercase;">${unionFullName}</p>
                  <span style="margin: 0; font-size: 0.93rem; font-weight: 400; line-height: 1.18; font-family: 'Segoe UI', Arial, Helvetica, sans-serif; color: #fff;">No. 262, 15th Ave., Brgy. Silangan, Cubao, Quezon City</span>
                </div>
              </div>
              <div class="digital-id-card__member-banner" style="background: ${(branding.shortName === 'LWU' ? '#1746b0' : bannerGradient)}; color: #fff; font-weight: 700; font-size: 1.05rem; letter-spacing: 0.13em; text-transform: uppercase; text-align: center; font-family: 'Segoe UI', Arial, Helvetica, sans-serif; padding: 13px 0 13px 0; width: 100%; display: flex; align-items: center; justify-content: center; border-radius: 0;">${(data.position || 'MEMBER').toUpperCase()}</div>
              <div class="digital-id-card__body" style="padding: 24px 24px 28px;">
                <div class="digital-id-card__photo">${photoBlock}</div>
                <div class="digital-id-card__info" style="flex:1; display:flex; flex-direction:column; width:100%;">
                  <div style="font-size:${fontSize}; white-space:nowrap; overflow:visible; text-overflow:clip; width:100%; display:block; text-align:center; line-height:1.2; margin:0; letter-spacing:0.05em; color:#0f172a; font-weight:700;">${data.displayName}</div>
                  <div style="font-size:0.75rem; font-weight:700; color:#475569; text-align:center; text-transform:uppercase; letter-spacing:0.05em; margin-top:2px;">${data.position}</div>
                  <div class="digital-id-card__meta" style="width:100%; margin-top:20px;">
                    <table style="margin:0 auto; font-size:0.8rem; color:#334155; border-spacing:0 2px; border-collapse:separate; text-align:left;">
                      <tbody>
                        <tr><td style="padding-right:10px; font-weight:500; color:#64748b;">ID Number</td><td style="padding-right:10px; color:#64748b;">:</td><td style="font-weight:600; color:#0f172a;">${data.shortId}</td></tr>
                        <tr><td style="padding-right:10px; font-weight:500; color:#64748b;">Company</td><td style="padding-right:10px; color:#64748b;">:</td><td style="font-weight:600; color:#0f172a;">${data.company}</td></tr>
                        <tr><td style="padding-right:10px; font-weight:500; color:#64748b;">Contact No.</td><td style="padding-right:10px; color:#64748b;">:</td><td style="font-weight:600; color:#0f172a;">${data.contact || "N/A"}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div class="digital-id-card__signature" style="margin-top:auto; padding-top:8px; width:100%; display:flex; flex-direction:column; align-items:center; gap:0;">
                    ${data.signature ? `<img src="${data.signature}" alt="Signature" style="height:62px; max-width:90%; object-fit:contain; display:block; margin-bottom:-2px;" />` : `<div style="height:62px;"></div>`}
                    <div style="width:70%; height:1px; background:rgba(15,23,42,0.6);"></div>
                    <span style="margin-top:6px; font-size:0.85rem; font-weight:600; color:rgba(15,23,42,0.7); text-align:center;">Authorized Signature</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="digital-id-card__face digital-id-card__face--back" data-card-back>
              <div class="digital-id-card__header digital-id-card__header--back" style="background: ${headerBackGradient};">
                <div class="digital-id-card__seal"><img src="${logoSrc}" alt="${unionFullName}" /></div>
                <div>
                  <p style="margin-bottom:0;">${branding.shortName || unionFullName} DIGITAL ID</p>
                  <span>Official Verification Code</span>
                </div>
              </div>
              <div class="digital-id-card__qr">
                <div class="digital-id-card__qr-box">${qrSrc ? `<img src="${qrSrc}" alt="QR" />` : ""}</div>
                <div class="digital-id-card__qr-info">
                  <span style="font-family:'IBM Plex Mono',monospace; font-size:0.95rem;">${data.membershipNo}</span>
                  <span style="font-size:0.8rem; font-family:monospace; color:#6b7280;">QR Ref: ${data.qrRef}</span>
                  <small style="color:#475569; font-size:0.8rem;">Scan to verify membership status</small>
                </div>
                <div class="digital-id-card__emergency-contact">
                  <strong>Emergency Contact</strong>
                  <span>Name: ${data.emergencyContactName || 'Not Provided'}</span>
                  <span>Mobile No: ${data.emergencyContactMobile || 'Not Provided'}</span>
                </div>
              </div>
              <div class="digital-id-card__footer" style="background: ${footerGradient}; color: #fff; display: flex; align-items: center; justify-content: center; gap: 10px; padding: 18px 22px; font-size: 0.80rem; letter-spacing: 0.05em;">
                <span>${branding.shortName || unionFullName} \u00A9 2026 \u2022 Official Digital Identification</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    `;
  };

  const captureCardImages = async (item) => {
    const cardData = toCardData(item);

    // Generate QR code with full payload matching Digital ID page format
    let qrFallback = cardData.qrImage;
    if (!qrFallback) {
      const qrPayload = {
        type: 'ALU_MEMBER_ID',
        version: 1,
        memberId: cardData.membershipNo,
        shortId: cardData.shortId,
        userId: item.userId || item.id,
        fullName: cardData.displayName,
        company: cardData.company,
        union: cardData.unionName,
        photo: cardData.photo,
        issuedAt: item.membershipDate || item.issuedAt || null,
      };
      const qrDataString = JSON.stringify(qrPayload);
      qrFallback = await QRCode.toDataURL(qrDataString, { width: 280, margin: 1 });
    }
    const cardMarkup = buildTemplateHtml({ ...cardData, qrImage: qrFallback });
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-9999px";
    container.style.top = "0";
    container.style.width = "900px";
    container.style.height = "auto";
    container.style.minHeight = "1000px";
    container.style.padding = "24px";
    container.style.zIndex = "-1";
    container.style.background = "#fff";
    container.style.overflow = "visible";

    const styleEl = document.createElement("style");
    styleEl.textContent = DIGITAL_ID_CSS;
    document.head.appendChild(styleEl);

    container.insertAdjacentHTML("beforeend", cardMarkup);
    document.body.appendChild(container);

    let result = null;
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      await waitForImages(container);

      const { front, back } = pickCardFaces(container);
      const frontEl = front || container.querySelector("[data-card-front]") || container.firstElementChild || container;
      const backEl = back || container.querySelector("[data-card-back]") || frontEl;

      // Fixed card dimensions matching the CSS aspect-ratio: 2 / 3.35
      const CARD_W = 380;
      const CARD_H = Math.round(CARD_W * (3.35 / 2)); // 637px

      // Force both faces to the same fixed dimensions
      [frontEl, backEl].forEach((el) => {
        if (!el || !el.style) return;
        el.style.position = "relative";
        el.style.display = "flex";
        el.style.flexDirection = "column";
        el.style.visibility = "visible";
        el.style.transform = "none";
        el.style.backfaceVisibility = "visible";
        el.style.width = `${CARD_W}px`;
        el.style.height = `${CARD_H}px`;
        el.style.minHeight = `${CARD_H}px`;
        el.style.maxHeight = `${CARD_H}px`;
        el.style.overflow = "hidden";
      });

      // Wait for layout to settle
      await new Promise((resolve) => setTimeout(resolve, 100));

      const scale = 3;

      const applyFaceStyles = (doc, showFront) => {
        const frontFace = doc.querySelector(".digital-id-card__face--front") || doc.querySelector("[data-card-front]");
        const backFace = doc.querySelector(".digital-id-card__face--back") || doc.querySelector("[data-card-back]");
        [frontFace, backFace].forEach((face) => {
          if (!face) return;
          face.style.position = "relative";
          face.style.width = `${CARD_W}px`;
          face.style.height = `${CARD_H}px`;
          face.style.minHeight = `${CARD_H}px`;
          face.style.maxHeight = `${CARD_H}px`;
          face.style.transform = "none";
          face.style.backfaceVisibility = "visible";
          face.style.overflow = "hidden";
        });
        if (frontFace) {
          frontFace.style.visibility = showFront ? "visible" : "hidden";
          frontFace.style.display = showFront ? "flex" : "none";
        }
        if (backFace) {
          backFace.style.visibility = showFront ? "hidden" : "visible";
          backFace.style.display = showFront ? "none" : "flex";
        }
        const sealImages = doc.querySelectorAll(".digital-id-card__seal img");
        sealImages.forEach((img) => {
          img.style.width = "76px";
          img.style.height = "76px";
          img.style.maxWidth = "76px";
          img.style.maxHeight = "76px";
          img.style.objectFit = "contain";
          img.style.objectPosition = "center";
          img.style.display = "block";
        });
      };

      const canvasFront = await html2canvas(frontEl, {
        scale,
        useCORS: true,
        backgroundColor: "#ffffff",
        width: CARD_W,
        height: CARD_H,
        logging: false,
        onclone: (doc) => applyFaceStyles(doc, true),
      });

      const canvasBack = await html2canvas(backEl, {
        scale,
        useCORS: true,
        backgroundColor: "#ffffff",
        width: CARD_W,
        height: CARD_H,
        logging: false,
        onclone: (doc) => applyFaceStyles(doc, false),
      });

      result = {
        frontUrl: canvasFront.toDataURL("image/png"),
        backUrl: canvasBack.toDataURL("image/png"),
      };
    } finally {
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
      if (styleEl.parentNode) {
        styleEl.parentNode.removeChild(styleEl);
      }
    }

    return result;
  };

  const openPrintPreview = (frontUrl, backUrl, filename = "LWU-ID") => {
    if (!frontUrl || !backUrl) return;

    const docHtml = `
      <html>
        <head>
          <title>${filename}</title>
          <style>
            @page { size: 86mm 137mm; margin: 0; }
            body { margin: 0; padding: 0; }
            .print-card { page-break-after: always; page-break-inside: avoid; display: flex; justify-content: center; align-items: center; width: 86mm; height: 137mm; margin: 0; padding: 0; overflow: hidden; }
            .print-card:last-child { page-break-after: auto; }
            .print-card img { width: 86mm; height: 137mm; display: block; }
            @media screen {
              body { background: #f8fafc; padding: 16px; display: flex; flex-direction: column; align-items: center; gap: 16px; }
              .print-card { box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12); border-radius: 12px; overflow: hidden; }
            }
          </style>
        </head>
        <body>
          <div class="print-card"><img src="${frontUrl}" alt="LWU ID Front" /></div>
          <div class="print-card"><img src="${backUrl}" alt="LWU ID Back" /></div>
        </body>
      </html>
    `;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.visibility = "hidden";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow?.document;
    if (!iframeDoc) {
      document.body.removeChild(iframe);
      alert("Unable to open print preview. Please check your browser settings.");
      return;
    }

    iframeDoc.open();
    iframeDoc.write(docHtml);
    iframeDoc.close();

    const triggerPrint = () => {
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (error) {
          console.error('Print failed:', error);
        } finally {
          setTimeout(() => {
            if (iframe.parentNode) {
              document.body.removeChild(iframe);
            }
          }, 1000);
        }
      }, 250);
    };

    if (iframe.contentWindow?.document?.readyState === "complete") {
      triggerPrint();
      return;
    }

    iframe.onload = triggerPrint;
  };

  const openBulkPrintPreview = (pages = [], filename = "LWU-ID-BULK") => {
    if (!Array.isArray(pages) || pages.length === 0) return;

    const pageMarkup = pages
      .map((page, index) => `<div class="print-card"><img src="${page}" alt="LWU ID ${index + 1}" /></div>`)
      .join("");

    const docHtml = `
      <html>
        <head>
          <title>${filename}</title>
          <style>
            @page { size: 86mm 137mm; margin: 0; }
            body { margin: 0; padding: 0; }
            .print-card { page-break-after: always; page-break-inside: avoid; display: flex; justify-content: center; align-items: center; width: 86mm; height: 137mm; margin: 0; padding: 0; overflow: hidden; }
            .print-card:last-child { page-break-after: auto; }
            .print-card img { width: 86mm; height: 137mm; display: block; }
            @media screen {
              body { background: #f8fafc; padding: 16px; display: flex; flex-direction: column; align-items: center; gap: 16px; }
              .print-card { box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12); border-radius: 12px; overflow: hidden; }
            }
          </style>
        </head>
        <body>
          ${pageMarkup}
        </body>
      </html>
    `;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.visibility = "hidden";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow?.document;
    if (!iframeDoc) {
      document.body.removeChild(iframe);
      alert("Unable to open print preview. Please check your browser settings.");
      return;
    }

    iframeDoc.open();
    iframeDoc.write(docHtml);
    iframeDoc.close();

    const triggerPrint = () => {
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (error) {
          console.error('Bulk print failed:', error);
        } finally {
          setTimeout(() => {
            if (iframe.parentNode) {
              document.body.removeChild(iframe);
            }
          }, 1000);
        }
      }, 250);
    };

    if (iframe.contentWindow?.document?.readyState === "complete") {
      triggerPrint();
      return;
    }

    iframe.onload = triggerPrint;
  };

  const handlePrint = async (item) => {
    if (!item) return;
    setPrintLoadingId(item.id);
    try {
      // Keep backend call for logging/authorization; ignore payload because we render our own template.
      await client
        .get(`/api/admin/id-cards/${item.id}/print?source=${item.cardType === "digital" ? "digital" : "request"}`, {
          responseType: "text",
        })
        .catch(() => null);

      const images = await captureCardImages(item);
      if (images?.frontUrl && images?.backUrl) {
        openPrintPreview(images.frontUrl, images.backUrl, `LWU-ID-${item.requestNo || item.id}`);
      } else {
        alert("Could not build print preview for this card.");
      }
      await fetchOverview();
    } catch (err) {
      console.error("Print failed", err);
      alert(err?.response?.data?.message ?? "Unable to load printable ID.");
    } finally {
      setPrintLoadingId(null);
    }
  };

  const handleBulkPrint = async () => {
    if (!selectedQueueIds.length) {
      alert("Select at least one request for bulk printing.");
      return;
    }

    const selectedItems = queue.filter((item) => selectedQueueIds.includes(String(item.id)));
    if (!selectedItems.length) {
      alert("No selected requests were found.");
      return;
    }

    setBulkPrintLoading(true);
    try {
      const pages = [];

      for (const item of selectedItems) {
        await client
          .get(`/api/admin/id-cards/${item.id}/print?source=${item.cardType === "digital" ? "digital" : "request"}`, {
            responseType: "text",
          })
          .catch(() => null);

        const images = await captureCardImages(item);
        if (images?.frontUrl && images?.backUrl) {
          pages.push(images.frontUrl, images.backUrl);
        }
      }

      if (!pages.length) {
        alert("Could not build print preview for selected cards.");
        return;
      }

      openBulkPrintPreview(pages, `LWU-ID-BULK-${Date.now()}`);
    } catch (err) {
      console.error("Bulk print failed", err);
      alert(err?.response?.data?.message ?? "Unable to bulk print selected IDs.");
    } finally {
      setBulkPrintLoading(false);
    }
  };

  const handlePrintWaybill = async (item) => {
    if (!item?.id) {
      alert('Waybill is not available for this request.');
      return;
    }

    setWaybillModal({
      open: true,
      item,
      html: '',
      loading: true,
      error: null,
    });

    try {
      const response = await client.get(`/api/admin/id-cards/${item.id}/waybill`, {
        responseType: 'text',
      });

      const html = String(response?.data || '').trim();
      if (!html) {
        setWaybillModal((previous) => ({
          ...previous,
          loading: false,
          error: 'Waybill HTML is empty. Please try again.',
        }));
        return;
      }

      setWaybillModal((previous) => ({
        ...previous,
        html,
        loading: false,
        error: null,
      }));
    } catch (err) {
      console.error('Waybill print failed', err);
      setWaybillModal((previous) => ({
        ...previous,
        loading: false,
        error: err?.response?.data?.message || 'Unable to load waybill preview.',
      }));
    }
  };

  const closeWaybillModal = () => {
    setWaybillModal({
      open: false,
      item: null,
      html: '',
      loading: false,
      error: null,
    });
  };

  const printWaybillFromModal = () => {
    try {
      const frameWindow = waybillFrameRef.current?.contentWindow;
      if (!frameWindow) {
        alert('Waybill preview is still loading. Please try again.');
        return;
      }
      frameWindow.focus();
      frameWindow.print();
    } catch (error) {
      console.error('Waybill modal print failed', error);
      alert('Unable to print waybill from preview.');
    }
  };

  const runAction = async (itemId, actionFn, errorMessage = 'Action failed') => {
    setActionLoadingId(itemId);
    let success = false;
    try {
      await actionFn();
      success = true;
      await fetchOverview();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || err.message || errorMessage);
    } finally {
      setActionLoadingId(null);
    }
    return success;
  };

  const openMoveConfirm = (item) => setMoveConfirm({ open: true, item });
  const closeMoveConfirm = () => setMoveConfirm({ open: false, item: null });

  const toggleActionMenu = (event, itemId) => {
    event.stopPropagation();
    if (openActionMenuId === itemId) {
      setOpenActionMenuId(null);
      return;
    }
    setActionMenuPosition(computeFixedActionMenuPosition(event.currentTarget));
    setOpenActionMenuId(itemId);
  };

  const confirmMoveToPrinting = async () => {
    if (!moveConfirm.item) return;
    await runAction(
      moveConfirm.item.id,
      async () => {
        await client.patch(`/api/admin/id-cards/${moveConfirm.item.id}/status`, { status: 'printing' });
      },
      'Unable to move request to printing',
    );
    closeMoveConfirm();
  };

  const handleMarkReady = (item) => {
    const pickupCode = item.metadata?.pickupCode || `PCK-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    return runAction(
      item.id,
      async () => {
        await client.patch(`/api/admin/id-cards/${item.id}/status`, {
          status: 'ready_for_pickup',
          pickupCode,
        });
        await client.post(`/api/admin/id-cards/${item.id}/notify`, {
          channel: 'email',
          message: `Card ${item.requestNo} is ready for pickup. Pickup code: ${pickupCode}`,
        });
      },
      'Unable to mark ID as ready for pickup',
    );
  };

  const openReadyConfirm = (item) => setReadyConfirm({ open: true, item });
  const closeReadyConfirm = () => setReadyConfirm({ open: false, item: null });

  const confirmMarkReady = async () => {
    if (!readyConfirm.item) return;
    await handleMarkReady(readyConfirm.item);
    closeReadyConfirm();
  };

  const openReleaseModal = (item) => {
    const isStripeDelivery = normalizeText(item.paymentMethod) === 'stripe' && normalizeText(item.deliveryMethod) === 'delivery';
    const autoReference =
      item.paymentReference ||
      item.metadata?.stripeReceiptUrl ||
      item.metadata?.stripe?.receiptUrl ||
      item.stripePaymentIntentId ||
      item.metadata?.stripePaymentIntentId ||
      item.metadata?.stripe?.paymentIntentId ||
      "";

    setReleaseModal({
      open: true,
      item,
      mode: isStripeDelivery ? 'shipping' : 'cash',
      shippingStatus: item.shippingStatus || item.metadata?.shippingStatus || 'preparing',
      trackingNumber: item.trackingNumber || item.metadata?.trackingNumber || '',
      paymentReference: autoReference || item.metadata?.payment?.reference || "",
      paymentNotes: item.metadata?.payment?.notes || "",
    });
  };

  const closeReleaseModal = () => {
    setReleaseModal({
      open: false,
      item: null,
      mode: 'cash',
      shippingStatus: 'preparing',
      trackingNumber: '',
      paymentReference: "",
      paymentNotes: "",
    });
  };

  const openDetailsModal = (item) => {
    setDetailsModal({
      open: true,
      item,
    });
  };

  const closeDetailsModal = () => {
    setDetailsModal({
      open: false,
      item: null,
    });
  };

  const openRejectModal = (item) => {
    setRejectModal({ open: true, item, reason: '' });
  };

  const closeRejectModal = () => {
    setRejectModal({ open: false, item: null, reason: '' });
  };

  const handleRejectSubmit = async () => {
    if (!rejectModal.item) return;
    const reason = (rejectModal.reason || '').trim();
    if (!reason) {
      alert('Please provide a rejection reason.');
      return;
    }

    const success = await runAction(
      rejectModal.item.id,
      async () => {
        await client.post(`/api/admin/id-cards/${rejectModal.item.id}/reject`, {
          rejectionReason: reason,
        });
      },
      'Unable to reject ID card request',
    );

    if (success) {
      closeRejectModal();
    }
  };

  const handleReleaseSubmit = async (event) => {
    event?.preventDefault();
    if (!releaseModal.item) return;

    if (releaseModal.mode === 'shipping') {
      const statusValue = normalizeText(releaseModal.shippingStatus) === 'shipping'
        ? 'shipped'
        : normalizeText(releaseModal.shippingStatus) || 'preparing';

      if (statusValue === 'shipped' && (releaseModal.trackingNumber || '').trim().length < 3) {
        alert('Tracking number is required when marking as shipped.');
        return;
      }

      const success = await runAction(
        releaseModal.item.id,
        async () => {
          await client.patch(`/api/admin/id-cards/${releaseModal.item.id}/shipping`, {
            shippingStatus: statusValue,
            trackingNumber: (releaseModal.trackingNumber || '').trim() || null,
          });
        },
        'Unable to update shipping status',
      );

      if (success) {
        closeReleaseModal();
      }
      return;
    }

    const paymentRef = (releaseModal.paymentReference || "").trim();
    const isValidRef = paymentRef.length >= 3;
    if (!isValidRef) {
      alert("Please enter a valid OR / receipt number before releasing.");
      return;
    }

    const expectedRefRaw =
      releaseModal.item.metadata?.payment?.reference ||
      releaseModal.item.paymentReference ||
      releaseModal.item.metadata?.paymentReference ||
      releaseModal.item.metadata?.pickupCode ||
      releaseModal.item.pickupCode ||
      null;
    const expectedRef = normalizeRef(expectedRefRaw);
    const enteredRef = normalizeRef(paymentRef);

    if (!expectedRef) {
      alert("This request has no recorded OR / receipt number. Please set the correct OR for this request before releasing.");
      return;
    }

    if (enteredRef !== expectedRef) {
      alert("The OR / receipt number does not match this request.");
      return;
    }

    const success = await runAction(
      releaseModal.item.id,
      async () => {
        await client.patch(`/api/admin/id-cards/${releaseModal.item.id}/release`, {
          paymentReference: paymentRef,
          paymentNotes: releaseModal.paymentNotes || null,
        });
      },
      'Unable to record cash payment',
    );

    if (success) {
      closeReleaseModal();
    }
  };

  useEffect(() => {
    fetchOverview();
    fetchCardPriceSettings();
  }, []);

  useEffect(() => {
    setSelectedQueueIds((previous) => previous.filter((id) => queue.some((item) => String(item.id) === String(id))));
  }, [queue]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (openActionMenuId && !event.target.closest('.admin-action-menu')) {
        setOpenActionMenuId(null);
      }
    };

    const onViewportChange = () => {
      setOpenActionMenuId(null);
    };

    document.addEventListener('mousedown', onClickOutside);
    window.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('scroll', onViewportChange, true);
      window.removeEventListener('resize', onViewportChange, true);
    };
  }, [openActionMenuId]);

  const expectedReleaseRef = normalizeRef(
    releaseModal.item?.metadata?.payment?.reference ||
      releaseModal.item?.paymentReference ||
      releaseModal.item?.metadata?.paymentReference ||
      releaseModal.item?.metadata?.pickupCode ||
      releaseModal.item?.pickupCode ||
      null,
  );
  const enteredReleaseRef = normalizeRef(releaseModal.paymentReference || "");
  const releaseRefMismatch = Boolean(expectedReleaseRef && enteredReleaseRef && expectedReleaseRef !== enteredReleaseRef);
  const releaseRefMissing = !expectedReleaseRef;
  const isShippingInProgress = (item) => {
    const metadata = item?.metadata || {};
    const isStripeDelivery = normalizeText(item?.paymentMethod) === 'stripe' && normalizeText(item?.deliveryMethod) === 'delivery';
    if (!isStripeDelivery) return false;

    const shippingStatus = normalizeText(item?.shippingStatus || metadata?.shippingStatus || metadata?.fulfillment?.shippingStatus || '');
    return ['preparing', 'shipped'].includes(shippingStatus);
  };

  const activeQueue = useMemo(() => {
    return queue.filter((item) => {
      const status = normalizeText(item?.status);
      if (['pending', 'printing', 'ready_for_pickup', 'rejected'].includes(status)) {
        return true;
      }
      return status === 'released' && isShippingInProgress(item);
    });
  }, [queue]);

  const completedQueue = useMemo(() => {
    return queue.filter((item) => {
      const metadata = item?.metadata || {};
      const status = normalizeText(item?.status);
      const shippingStatus = normalizeText(item?.shippingStatus || metadata?.shippingStatus || metadata?.fulfillment?.shippingStatus || '');

      if (status === 'delivered' || shippingStatus === 'delivered') {
        return true;
      }

      if (status === 'released' && !isShippingInProgress(item)) {
        return true;
      }

      return false;
    });
  }, [queue]);

  const filteredActiveQueue = useMemo(() => {
    const query = normalizeText(searchQuery);

    return activeQueue.filter((item) => {
      const metadata = item.metadata || {};
      const itemStatus = normalizeText(item.status);
      const isPending = itemStatus === "pending";
      const isPrinting = itemStatus === "printing";
      const isReadyForPickup = itemStatus === "ready_for_pickup";
      const isRejected = itemStatus === "rejected";
      const isShipping = isShippingInProgress(item);

      let matchesWorkflow = true;
      if (workflowFilter === "pending") {
        matchesWorkflow = isPending;
      } else if (workflowFilter === "printing_ids") {
        matchesWorkflow = isPrinting;
      } else if (workflowFilter === "mark_and_notify") {
        matchesWorkflow = isPrinting;
      } else if (workflowFilter === "ready_to_pickup") {
        matchesWorkflow = isReadyForPickup;
      } else if (workflowFilter === "releasing") {
        matchesWorkflow = isReadyForPickup || isShipping;
      } else if (workflowFilter === "rejected") {
        matchesWorkflow = isRejected;
      }

      if (!matchesWorkflow) return false;
      if (!query) return true;

      const memberName = item.memberName || metadata.memberName || item.member || "";
      const membershipNo = item.membershipNo || metadata.membershipNo || "";
      const pickupCode = metadata.pickupCode || item.pickupCode || "";
      const requestNo = item.requestNo || "";
      const statusLabel = item.statusLabel || formatLabel(item.status, item.status);

      return [memberName, membershipNo, pickupCode, requestNo, statusLabel]
        .map(normalizeText)
        .some((value) => value.includes(query));
    });
  }, [activeQueue, searchQuery, workflowFilter]);

  const filteredCompletedQueue = useMemo(() => {
    const query = normalizeText(releasedSearchQuery);

    const workflowFilteredQueue = completedQueue.filter((item) => {
      const metadata = item.metadata || {};
      const status = normalizeText(item.status);
      const shippingStatus = normalizeText(item.shippingStatus || metadata.shippingStatus || metadata?.fulfillment?.shippingStatus || '');
      const isStripeDelivery = normalizeText(item.paymentMethod) === 'stripe' && normalizeText(item.deliveryMethod) === 'delivery';

      if (completedWorkflowFilter === 'released') {
        return status === 'released' && shippingStatus !== 'delivered';
      }

      if (completedWorkflowFilter === 'delivered') {
        return shippingStatus === 'delivered' || status === 'delivered';
      }

      if (completedWorkflowFilter === 'stripe_delivery') {
        return isStripeDelivery;
      }

      if (completedWorkflowFilter === 'cash_pickup') {
        return !isStripeDelivery;
      }

      return true;
    });

    if (!query) return workflowFilteredQueue;

    return workflowFilteredQueue.filter((item) => {
      const metadata = item.metadata || {};
      const memberName = item.memberName || metadata.memberName || item.member || '';
      const membershipNo = item.membershipNo || metadata.membershipNo || '';
      const pickupCode = metadata.pickupCode || item.pickupCode || '';
      const trackingNumber = item.trackingNumber || metadata.trackingNumber || '';
      const requestNo = item.requestNo || '';
      const statusLabel = item.statusLabel || formatLabel(item.status, item.status);

      return [memberName, membershipNo, pickupCode, trackingNumber, requestNo, statusLabel]
        .map(normalizeText)
        .some((value) => value.includes(query));
    });
  }, [completedQueue, releasedSearchQuery, completedWorkflowFilter]);

  const PAGE_SIZE = 10;
  const activeTotalPages = Math.max(1, Math.ceil(filteredActiveQueue.length / PAGE_SIZE));
  const completedTotalPages = Math.max(1, Math.ceil(filteredCompletedQueue.length / PAGE_SIZE));
  const paginatedActiveQueue = filteredActiveQueue.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE);
  const paginatedCompletedQueue = filteredCompletedQueue.slice((completedPage - 1) * PAGE_SIZE, completedPage * PAGE_SIZE);

  useEffect(() => {
    setActivePage(1);
  }, [searchQuery, workflowFilter]);

  useEffect(() => {
    setCompletedPage(1);
  }, [releasedSearchQuery, completedWorkflowFilter]);

  useEffect(() => {
    if (activePage > activeTotalPages) {
      setActivePage(activeTotalPages);
    }
  }, [activePage, activeTotalPages]);

  useEffect(() => {
    if (completedPage > completedTotalPages) {
      setCompletedPage(completedTotalPages);
    }
  }, [completedPage, completedTotalPages]);

  const allQueueSelected =
    paginatedActiveQueue.length > 0 && paginatedActiveQueue.every((item) => selectedQueueIds.includes(String(item.id)));

  const isWorkflowFilterActive = workflowFilter !== 'all';

  const isCompletedWorkflowFilterActive = completedWorkflowFilter !== 'all';

  const toggleSelectAll = () => {
    const visibleIds = paginatedActiveQueue.map((item) => String(item.id));
    if (allQueueSelected) {
      setSelectedQueueIds((previous) => previous.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedQueueIds((previous) => Array.from(new Set([...previous, ...visibleIds])));
  };

  const toggleSelectRow = (itemId) => {
    const normalizedId = String(itemId);
    setSelectedQueueIds((previous) => (
      previous.includes(normalizedId)
        ? previous.filter((id) => id !== normalizedId)
        : [...previous, normalizedId]
    ));
  };

  return (
    <div className="admin-page admin-stack-lg id-card-management-page">
      <header className="admin-surface idm-hero">
        <div className="idm-hero__intro">
          <span className="idm-hero__eyebrow">ID Operations</span>
          <h1>ID Card Management</h1>
          <p className="admin-muted">
            Manage physical and digital membership IDs, including replacements and activation status.
          </p>
        </div>
        <div className="admin-actions idm-hero__actions">
          <button
            type="button"
            className={`admin-icon-button admin-icon-button--refresh ${loading ? "is-loading" : ""}`}
            onClick={fetchOverview}
            disabled={loading}
            aria-label="Refresh ID card overview"
            title="Refresh ID card overview"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </header>

      {error ? <div className="admin-alert is-error">{error}</div> : null}
        <div className="admin-card-grid cols-3 idm-summary-grid">
        <article className="admin-card">
          <div className="admin-card__label">Total card requests</div>
          <div className="admin-row">
            <div className="admin-card__value">{metrics.totalRequests.toLocaleString()}</div>
            <span className="admin-chip is-blue"><FileText size={18} /></span>
          </div>
          <div className="admin-card__meta">Requests recorded in system</div>
        </article>
        <article className="admin-card">
          <div className="admin-card__label">Physical cards queued</div>
          <div className="admin-row">
            <div className="admin-card__value">{metrics.physicalQueued.toLocaleString()}</div>
            <span className="admin-chip is-orange"><Clock size={18} /></span>
          </div>
          <div className="admin-card__meta">In production with print vendor</div>
        </article>
        <article className="admin-card">
          <div className="admin-card__label">Ready for pickup</div>
          <div className="admin-row">
            <div className="admin-card__value">{metrics.readyForPickup.toLocaleString()}</div>
            <span className="admin-chip is-green"><CheckCircle2 size={18} /></span>
          </div>
          <div className="admin-card__meta">Cards awaiting member pickup</div>
        </article>
        </div>

      <section className="admin-surface admin-stack-md id-card-pricing idm-section idm-section--pricing">
        <div className="admin-row id-card-pricing__row">
          <div className="id-card-pricing__intro">
            <div className="admin-card__label">Physical ID Card Pricing (PHP)</div>
            <p className="admin-muted id-card-pricing__intro-text">Pickup total uses card price only. Delivery total uses card price plus shipping fee.</p>
          </div>
          <div className="id-card-pricing__controls">
            <div className="admin-field id-card-pricing__field">
              <span>Card Price</span>
              <input
                className="id-card-pricing__input"
                type="number"
                min="0"
                step="0.01"
                value={cardPricePhp}
                onChange={(event) => setCardPricePhp(event.target.value)}
                disabled={settingsLoading || settingsSaving || !isEditingPricing}
              />
            </div>
            <div className="admin-field id-card-pricing__field">
              <span>Shipping Fee</span>
              <input
                className="id-card-pricing__input"
                type="number"
                min="0"
                step="0.01"
                value={idShippingFeePhp}
                onChange={(event) => setIdShippingFeePhp(event.target.value)}
                disabled={settingsLoading || settingsSaving || !isEditingPricing}
              />
            </div>
            <button
              type="button"
              className="admin-button is-primary id-card-pricing__edit-button"
              onClick={handlePricingAction}
              disabled={settingsLoading || settingsSaving}
            >
              {settingsSaving ? <Loader2 size={16} className="admin-spin" /> : (isEditingPricing ? <Save size={16} /> : <Pencil size={16} />)}
              {settingsSaving ? 'Saving…' : (isEditingPricing ? 'Save' : 'Edit')}
            </button>
          </div>
        </div>
        {settingsError ? <p className="admin-alert is-error id-card-pricing__status-alert">{settingsError}</p> : null}
        {settingsSuccess ? <p className="admin-alert is-success id-card-pricing__status-alert">{settingsSuccess}</p> : null}
      </section>

      <section className="admin-surface admin-stack-md idm-section idm-section--active-queue">
        <div className="admin-heading-inline">
          <div>
            <h2>Production queue</h2>
            <p className="admin-muted">Track progress across physical printing and digital activation.</p>
          </div>
        </div>
        <div className="admin-row id-card-queue__controls-row">
          <div className="id-card-queue__search-field">
            <div className="admin-card id-card-queue__search-card">
              <Search size={18} color="#64748b" />
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by request ID, member, membership no, or pickup code"
                className="id-card-queue__search-input"
                aria-label="Search requests"
              />
            </div>
          </div>
          <div className="id-card-queue__filter-field">
            <label className="id-card-queue__filter-label" htmlFor="id-card-active-workflow-filter">
              <Filter size={14} /> Filter
            </label>
            <div className={`id-card-queue__filter-select-wrap ${isWorkflowFilterActive ? 'is-active' : ''}`.trim()}>
              <select
                id="id-card-active-workflow-filter"
                className="id-card-queue__filter-select"
                value={workflowFilter}
                onChange={(event) => setWorkflowFilter(event.target.value)}
                aria-label="Production queue workflow filter"
                title="Production queue workflow filter"
              >
                {WORKFLOW_FILTERS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="id-card-queue__filter-chevron" aria-hidden="true" />
            </div>
            {isWorkflowFilterActive ? (
              <button
                type="button"
                className="admin-button admin-button--ghost id-card-queue__filter-clear"
                onClick={() => setWorkflowFilter('all')}
                aria-label="Clear production queue filter"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>
        <div className="admin-stack-sm id-card-queue__selection-wrap">
          <div className="admin-row id-card-queue__selection-row">
            <div className="admin-row id-card-queue__selection-left">
              <label className="admin-muted id-card-queue__select-label">
                <input
                  type="checkbox"
                  checked={allQueueSelected}
                  onChange={toggleSelectAll}
                  aria-label="Select all requests"
                  disabled={bulkPrintLoading || paginatedActiveQueue.length === 0}
                  className="id-card-queue__select-input"
                />
                Select all requests
              </label>
              <span className={`admin-chip ${selectedQueueIds.length ? "is-blue" : ""}`.trim()}>
                {selectedQueueIds.length} selected
              </span>
            </div>
            <div className="admin-row id-card-queue__selection-actions">
            <button
              type="button"
              className="admin-button"
              onClick={() => setSelectedQueueIds([])}
              disabled={bulkPrintLoading || selectedQueueIds.length === 0}
            >
              Clear Selection
            </button>
            <button
              type="button"
              className="admin-button is-primary"
              onClick={handleBulkPrint}
              disabled={bulkPrintLoading || selectedQueueIds.length === 0 || paginatedActiveQueue.length === 0}
            >
              {bulkPrintLoading ? <Loader2 size={16} className="admin-spin" /> : <PrinterIcon size={16} />}
              {bulkPrintLoading ? "Printing\u2026" : "Bulk Print Selected"}
            </button>
            </div>
          </div>
        </div>
        <div className="admin-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="id-card-queue__th-select">Select</th>
                <th>Request ID</th>
                <th>Member</th>
                <th>Request Date</th>
                <th>Payment Status</th>
                <th>Card Status</th>
                <th>Pickup / Tracking</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredActiveQueue.length === 0 ? (
                <tr>
                  <td colSpan={8} className="admin-empty">
                    {loading
                      ? "Loading queue..."
                      : activeQueue.length === 0
                        ? "No active ID card requests in queue."
                        : "No active ID card requests matched your search/filter."}
                  </td>
                </tr>
              ) : (
                paginatedActiveQueue.map((item) => {
                  const metadata = item.metadata || {};
                  const memberName = item.memberName || metadata.memberName || item.member || 'Member';
                  const membershipNo = item.membershipNo || metadata.membershipNo || '';
                  const paymentStatus = item.paymentStatus || metadata.paymentStatus || 'pending';
                  const paymentStatusLabel = item.paymentStatusLabel || formatLabel(paymentStatus, 'Pending');
                  const paymentTimestamp = item.paymentCollectedAt || metadata.payment?.recordedAt || metadata.paymentCollectedAt;
                  const isStripeDelivery = normalizeText(item.paymentMethod) === 'stripe' && normalizeText(item.deliveryMethod) === 'delivery';
                  const shippingState = normalizeText(item.shippingStatus || metadata.shippingStatus || metadata?.fulfillment?.shippingStatus || 'preparing');
                  const canUpdateShipping = isStripeDelivery && ['preparing', 'shipped'].includes(shippingState);
                  const paymentDisplay =
                    item.paymentStatusDisplay
                      ? `${item.paymentStatusDisplay}${paymentTimestamp ? ` (${new Date(paymentTimestamp).toLocaleString()})` : ''}`
                      : (
                        paymentStatus === 'paid'
                          ? `Cash Paid${paymentTimestamp ? ` (${new Date(paymentTimestamp).toLocaleString()})` : ''}`
                          : paymentStatusLabel
                      );

                  const pickupCode = isStripeDelivery
                    ? (item.trackingNumber || metadata.trackingNumber || '\u2014')
                    : (metadata.pickupCode || '\u2014');
                  const statusLabel = item.statusLabel || formatLabel(item.status, item.status);
                  const adminStatusLabel = isStripeDelivery && shippingState === 'delivered' ? 'Delivered' : statusLabel;
                  const isBusy = actionLoadingId === item.id || printLoadingId === item.id;
                  const isSelected = selectedQueueIds.includes(String(item.id));
                  const requestDateDisplay = formatRequestDate(
                    item.requestedAt || metadata.requestedAt || item.requestedOn || metadata.requestedOn,
                  );
                  return (
                  <tr key={item.id}>
                    <td className="id-card-queue__td-select">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectRow(item.id)}
                        aria-label={`Select request ${item.requestNo}`}
                        disabled={bulkPrintLoading}
                        className="id-card-queue__row-checkbox"
                      />
                    </td>
                    <td className="admin-proponent__mono">{item.requestNo}</td>
                    <td>
                      <div className="admin-member">
                        <div>
                          <div className="admin-member__name">{memberName}</div>
                          <div className="admin-member__sub">{membershipNo}</div>
                        </div>
                      </div>
                    </td>
                    <td>{requestDateDisplay}</td>
                    <td>{paymentDisplay}</td>
                    <td><span className={`admin-chip ${normalizeText(item.status) === 'rejected' ? 'is-red' : 'is-blue'}`}>{adminStatusLabel}</span></td>
                    <td>

                      <div className="admin-muted"> {pickupCode}</div>
                    </td>
                    <td>
                      <div className="admin-table__actions admin-action-menu">
                        <button
                          type="button"
                          className="admin-action-button admin-action-menu-trigger"
                          onClick={(event) => toggleActionMenu(event, item.id)}
                          disabled={isBusy}
                        >
                          {isBusy ? <Loader2 size={16} className="admin-spin" /> : <MoreVertical size={18} />}
                        </button>
                        {openActionMenuId === item.id ? (
                          <div
                            className="admin-action-dropdown admin-action-dropdown--fixed"
                            style={{
                              "--admin-action-menu-top": `${actionMenuPosition.top}px`,
                              "--admin-action-menu-left": `${actionMenuPosition.left}px`,
                              "--admin-action-menu-max-height": `${actionMenuPosition.maxHeight}px`,
                            }}
                          >
                            {item.status === 'pending' ? (
                              <button
                                type="button"
                                className="admin-action-dropdown-item"
                                onClick={() => {
                                  openMoveConfirm(item);
                                  setOpenActionMenuId(null);
                                }}
                                disabled={isBusy}
                              >
                                <ChevronsRight size={16} /> Move to Printing
                              </button>
                            ) : null}
                            {item.status === 'printing' && !isStripeDelivery ? (
                              <button
                                type="button"
                                className="admin-action-dropdown-item"
                                onClick={() => {
                                  openReadyConfirm(item);
                                  setOpenActionMenuId(null);
                                }}
                                disabled={isBusy}
                              >
                                <CheckCircle2 size={16} /> Mark Ready & Notify
                              </button>
                            ) : null}
                            {canUpdateShipping ? (
                              <button
                                type="button"
                                className="admin-action-dropdown-item"
                                onClick={() => {
                                  openReleaseModal(item);
                                  setOpenActionMenuId(null);
                                }}
                                disabled={isBusy || !['paid', 'waived'].includes(normalizeText(item.paymentStatus))}
                              >
                                <ChevronsRight size={16} /> Update Shipping Status
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="admin-action-dropdown-item"
                              onClick={() => {
                                openDetailsModal(item);
                                setOpenActionMenuId(null);
                              }}
                              disabled={isBusy}
                            >
                              <Eye size={16} /> View Full Details
                            </button>
                            {isStripeDelivery ? (
                              <button
                                type="button"
                                className="admin-action-dropdown-item"
                                onClick={() => {
                                  handlePrintWaybill(item);
                                  setOpenActionMenuId(null);
                                }}
                                disabled={isBusy}
                              >
                                <FileText size={16} /> Print Waybill
                              </button>
                            ) : null}
                            {item.status === 'ready_for_pickup' ? (
                              <button
                                type="button"
                                className="admin-action-dropdown-item"
                                onClick={() => {
                                  openReleaseModal(item);
                                  setOpenActionMenuId(null);
                                }}
                                disabled={isBusy || isStripeDelivery}
                              >
                                <DollarSign size={16} /> Release & Record Cash
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="admin-action-dropdown-item"
                              onClick={() => {
                                handlePrint(item);
                                setOpenActionMenuId(null);
                              }}
                              disabled={isBusy}
                            >
                              <PrinterIcon size={16} /> Print Card
                            </button>
                            {['pending', 'printing'].includes(item.status) ? (
                              <button
                                type="button"
                                className="admin-action-dropdown-item id-card-action-dropdown-item--danger"
                                onClick={() => {
                                  openRejectModal(item);
                                  setOpenActionMenuId(null);
                                }}
                                disabled={isBusy}
                              >
                                <XCircle size={16} /> Reject
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )})
              )}
            </tbody>
          </table>
        </div>
        <footer className="admin-row id-card-queue__pager-footer id-card-queue__pager-footer--lined">
          <span className="admin-muted id-card-queue__pager-summary">
            Page {activePage} of {activeTotalPages}
          </span>
          <span className="admin-pill is-blue">
            Showing {paginatedActiveQueue.length} of {filteredActiveQueue.length}
          </span>
          <div className="admin-actions id-card-queue__pager-actions">
            <button
              type="button"
              className="admin-button admin-button--ghost id-card-queue__pager-button"
              onClick={() => setActivePage((prev) => Math.max(1, prev - 1))}
              disabled={activePage <= 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            <button
              type="button"
              className="admin-button admin-button--ghost id-card-queue__pager-button"
              onClick={() => setActivePage((prev) => Math.min(activeTotalPages, prev + 1))}
              disabled={activePage >= activeTotalPages}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </footer>
      </section>

      <section className="admin-surface admin-stack-md id-card-queue--completed idm-section idm-section--completed-queue">
        <div className="admin-heading-inline">
          <div>
            <h2>Completed / Released Queue</h2>
            <p className="admin-muted">Delivered and released IDs are archived here to keep production queue clean.</p>
          </div>
        </div>
        <div className="admin-row id-card-queue__controls-row">
          <div className="id-card-queue__search-field id-card-queue__search-field--released">
            <div className="admin-card id-card-queue__search-card">
              <Search size={18} color="#64748b" />
              <input
                type="text"
                value={releasedSearchQuery}
                onChange={(event) => setReleasedSearchQuery(event.target.value)}
                placeholder="Search released by request ID, member..."
                className="id-card-queue__search-input"
                aria-label="Search completed or released requests"
              />
            </div>
          </div>
          <div className="id-card-queue__filter-field">
            <label className="id-card-queue__filter-label" htmlFor="id-card-completed-workflow-filter">
              <Filter size={14} /> Filter
            </label>
            <div className={`id-card-queue__filter-select-wrap ${isCompletedWorkflowFilterActive ? 'is-active' : ''}`.trim()}>
              <select
                id="id-card-completed-workflow-filter"
                className="id-card-queue__filter-select"
                value={completedWorkflowFilter}
                onChange={(event) => setCompletedWorkflowFilter(event.target.value)}
                aria-label="Completed queue filters"
                title="Completed queue filters"
              >
                {COMPLETED_WORKFLOW_FILTERS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="id-card-queue__filter-chevron" aria-hidden="true" />
            </div>
            {isCompletedWorkflowFilterActive ? (
              <button
                type="button"
                className="admin-button admin-button--ghost id-card-queue__filter-clear"
                onClick={() => setCompletedWorkflowFilter('all')}
                aria-label="Clear completed queue filter"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>
        <div className="admin-scroll id-card-queue__completed-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Request ID</th>
                <th>Member</th>
                <th>Request Date</th>
                <th>Payment Status</th>
                <th>Completion Status</th>
                <th>Tracking / Pickup</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompletedQueue.length === 0 ? (
                <tr>
                  <td colSpan={7} className="admin-empty">
                    {loading
                      ? 'Loading completed queue...'
                      : completedQueue.length === 0
                        ? 'No released or delivered IDs yet.'
                        : 'No completed requests matched your search.'}
                  </td>
                </tr>
              ) : (
                paginatedCompletedQueue.map((item) => {
                  const metadata = item.metadata || {};
                  const memberName = item.memberName || metadata.memberName || item.member || 'Member';
                  const membershipNo = item.membershipNo || metadata.membershipNo || '';
                  const isStripeDelivery = normalizeText(item.paymentMethod) === 'stripe' && normalizeText(item.deliveryMethod) === 'delivery';
                  const shippingState = normalizeText(item.shippingStatus || metadata.shippingStatus || metadata?.fulfillment?.shippingStatus || '');
                  const completionLabel = shippingState === 'delivered' ? 'Delivered' : (item.statusLabel || formatLabel(item.status, item.status));
                  const paymentDisplay = item.paymentStatusDisplay || item.paymentStatusLabel || formatLabel(item.paymentStatus || 'paid', 'Paid');
                  const requestDateDisplay = formatRequestDate(
                    item.requestedAt || metadata.requestedAt || item.requestedOn || metadata.requestedOn,
                  );
                  const trackingOrPickup = isStripeDelivery
                    ? (item.trackingNumber || metadata.trackingNumber || '\u2014')
                    : (metadata.pickupCode || '\u2014');
                  const actionMenuId = `completed-${item.id}`;
                  const isBusy = actionLoadingId === item.id || printLoadingId === item.id;
                  return (
                    <tr key={`completed-${item.id}`}>
                      <td className="admin-proponent__mono">{item.requestNo}</td>
                      <td>
                        <div className="admin-member">
                          <div>
                            <div className="admin-member__name">{memberName}</div>
                            <div className="admin-member__sub">{membershipNo}</div>
                          </div>
                        </div>
                      </td>
                      <td>{requestDateDisplay}</td>
                      <td>{paymentDisplay}</td>
                      <td><span className="admin-chip is-green">{completionLabel}</span></td>
                      <td><span className="admin-muted">{trackingOrPickup}</span></td>
                      <td>
                        <div className="admin-table__actions admin-action-menu">
                          <button
                            type="button"
                            className="admin-action-button admin-action-menu-trigger"
                            onClick={(event) => toggleActionMenu(event, actionMenuId)}
                            disabled={isBusy}
                          >
                            {isBusy ? <Loader2 size={16} className="admin-spin" /> : <MoreVertical size={18} />}
                          </button>
                          {openActionMenuId === actionMenuId ? (
                            <div
                              className="admin-action-dropdown admin-action-dropdown--fixed"
                              style={{
                                "--admin-action-menu-top": `${actionMenuPosition.top}px`,
                                "--admin-action-menu-left": `${actionMenuPosition.left}px`,
                                "--admin-action-menu-max-height": `${actionMenuPosition.maxHeight}px`,
                              }}
                            >
                              <button
                                type="button"
                                className="admin-action-dropdown-item"
                                onClick={() => {
                                  openDetailsModal(item);
                                  setOpenActionMenuId(null);
                                }}
                                disabled={isBusy}
                              >
                                <Eye size={16} /> View Full Details
                              </button>
                              <button
                                type="button"
                                className="admin-action-dropdown-item"
                                onClick={() => {
                                  handlePrint(item);
                                  setOpenActionMenuId(null);
                                }}
                                disabled={isBusy}
                              >
                                <PrinterIcon size={16} /> Print Card
                              </button>
                              {isStripeDelivery ? (
                                <button
                                  type="button"
                                  className="admin-action-dropdown-item"
                                  onClick={() => {
                                    handlePrintWaybill(item);
                                    setOpenActionMenuId(null);
                                  }}
                                  disabled={isBusy}
                                >
                                  <FileText size={16} /> Print Waybill
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <footer className="admin-row id-card-queue__pager-footer id-card-queue__pager-footer--lined">
          <span className="admin-muted id-card-queue__pager-summary">
            Page {completedPage} of {completedTotalPages}
          </span>
          <span className="admin-pill is-blue">
            Showing {paginatedCompletedQueue.length} of {filteredCompletedQueue.length}
          </span>
          <div className="admin-actions id-card-queue__pager-actions">
            <button
              type="button"
              className="admin-button admin-button--ghost id-card-queue__pager-button"
              onClick={() => setCompletedPage((prev) => Math.max(1, prev - 1))}
              disabled={completedPage <= 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            <button
              type="button"
              className="admin-button admin-button--ghost id-card-queue__pager-button"
              onClick={() => setCompletedPage((prev) => Math.min(completedTotalPages, prev + 1))}
              disabled={completedPage >= completedTotalPages}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </footer>
      </section>

      {moveConfirm.open ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeMoveConfirm} role="presentation" />
          <div className="admin-dialog__panel admin-stack-md" role="document">
            <header className="admin-dialog__header">
              <h3>Move to Printing</h3>
              <button type="button" className="admin-icon-button" onClick={closeMoveConfirm} aria-label="Close">
                <X size={16} />
              </button>
            </header>
            <div className="admin-dialog__body admin-stack-md">
              <p className="admin-muted">
                Are you sure you want to move
                {' '}
                <strong>{moveConfirm.item?.requestNo || 'this request'}</strong>
                {' '}
                to printing?
              </p>
              <p className="admin-muted id-card-queue__dialog-hint">
                Member: {moveConfirm.item?.memberName || moveConfirm.item?.member || 'Member'}
              </p>
            </div>
            <footer className="admin-dialog__footer admin-dialog__footer--grid">
              <button
                type="button"
                className="admin-button admin-button--ghost admin-dialog__footer-back"
                onClick={closeMoveConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-button is-primary"
                onClick={confirmMoveToPrinting}
                disabled={actionLoadingId === moveConfirm.item?.id}
              >
                {actionLoadingId === moveConfirm.item?.id ? <Loader2 size={14} className="admin-spin" /> : null}
                Yes, Move to Printing
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {readyConfirm.open ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeReadyConfirm} role="presentation" />
          <div className="admin-dialog__panel admin-stack-md" role="document">
            <header className="admin-dialog__header">
              <h3>Mark Ready & Notify</h3>
              <button type="button" className="admin-icon-button" onClick={closeReadyConfirm} aria-label="Close">
                <X size={16} />
              </button>
            </header>
            <div className="admin-dialog__body admin-stack-md">
              <p className="admin-muted">
                Are you sure you want to mark
                {' '}
                <strong>{readyConfirm.item?.requestNo || 'this request'}</strong>
                {' '}
                as ready and notify the member?
              </p>
              <p className="admin-muted id-card-queue__dialog-hint">
                Member: {readyConfirm.item?.memberName || readyConfirm.item?.member || 'Member'}
              </p>
            </div>
            <footer className="admin-dialog__footer admin-dialog__footer--grid">
              <button
                type="button"
                className="admin-button admin-button--ghost admin-dialog__footer-back"
                onClick={closeReadyConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-button is-primary"
                onClick={confirmMarkReady}
                disabled={actionLoadingId === readyConfirm.item?.id}
              >
                {actionLoadingId === readyConfirm.item?.id ? <Loader2 size={14} className="admin-spin" /> : null}
                Yes, Mark Ready & Notify
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {detailsModal.open ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeDetailsModal} role="presentation" />
          <div className="admin-dialog__panel admin-stack-md" role="document" style={{ maxWidth: 760 }}>
            <header className="admin-dialog__header">
              <h3>Order Timeline / Full Details</h3>
              <button type="button" className="admin-icon-button" onClick={closeDetailsModal} aria-label="Close">
                <X size={16} />
              </button>
            </header>
            {(() => {
              const item = detailsModal.item || {};
              const metadata = item.metadata || {};
              const isStripeDelivery = normalizeText(item.paymentMethod) === 'stripe' && normalizeText(item.deliveryMethod) === 'delivery';
              const deliveryLabel = normalizeText(item.deliveryMethod) === 'delivery' ? 'Address Delivery' : 'Pickup at Office';
              const paymentMethodLabel = isStripeDelivery
                ? `Stripe${item.stripePaymentMethodLabel ? ` - ${item.stripePaymentMethodLabel}` : ''}`
                : 'Cash on Pickup';
              const receiptUrl = item.stripeReceiptUrl || metadata?.stripe?.receiptUrl || null;
              const totalPaid = item.paymentAmountPhp || metadata?.payment?.totalPhp || null;
              const requestedAt = item.requestedAt || metadata.requestedAt || null;
              const paidAt = item.paymentCollectedAt || metadata?.payment?.paidAt || metadata?.payment?.recordedAt || null;
              const fulfilledAt = item.fulfilledAt || metadata?.fulfillment?.releasedAt || metadata?.fulfilledAt || null;
              const processingAt = item.processedAt || metadata?.processing?.movedToPrintingAt || null;
              const shippedAt = item.shippedAt || metadata?.fulfillment?.shippedAt || null;
              const deliveredAt = item.deliveredAt || metadata?.fulfillment?.deliveredAt || item.fulfilledAt || null;
              const trackingNumber = item.trackingNumber || metadata?.fulfillment?.trackingNumber || metadata?.trackingNumber || null;
              const deliveryAddress = item.deliveryAddress || metadata?.deliveryAddress || null;
              const deliveryContactNumber = item.phone || metadata?.phone || metadata?.contactNumber || null;
              const pickupCode = metadata?.pickupCode || item.pickupCode || 'Not available';
              const paymentReference = item.paymentReference || metadata?.payment?.reference || metadata?.paymentReference || 'Not provided';

              const timelineRows = isStripeDelivery
                ? [
                    {
                      key: 'requested',
                      title: 'Requested',
                      when: requestedAt,
                      detail: `Delivery Method: ${deliveryLabel}\nDelivery Address: ${deliveryAddress || 'Not provided'}\nContact Number: ${deliveryContactNumber || 'Not provided'}`,
                    },
                    {
                      key: 'payment',
                      title: 'Payment',
                      when: paidAt,
                      detail: `Total Paid: ${formatPeso(totalPaid)} | Method: ${paymentMethodLabel}`,
                      link: receiptUrl,
                      linkLabel: 'Open Stripe Receipt',
                    },
                    {
                      key: 'processing',
                      title: 'Processing',
                      when: processingAt,
                      detail: 'Moved to Printing',
                    },
                    {
                      key: 'shipped',
                      title: 'Fulfillment - Shipped',
                      when: shippedAt,
                      detail: `Tracking Number: ${trackingNumber || 'Not yet assigned'}`,
                    },
                    {
                      key: 'delivered',
                      title: 'Fulfillment - Delivered',
                      when: deliveredAt,
                      detail: 'Delivery completed',
                    },
                  ]
                : [
                    {
                      key: 'requested',
                      title: 'Requested',
                      when: requestedAt,
                      detail: 'Delivery Method: Pickup at Office',
                    },
                    {
                      key: 'processing',
                      title: 'Processing',
                      when: processingAt,
                      detail: 'Moved to Printing',
                    },
                    {
                      key: 'ready_for_pickup',
                      title: 'Ready for Pickup',
                      when: metadata?.lastReadyAt || null,
                      detail: `Pickup Code: ${pickupCode}`,
                    },
                    {
                      key: 'payment_release',
                      title: 'Payment & Release',
                      when: paidAt || fulfilledAt,
                      detail: `Total Paid: ${formatPeso(totalPaid)} | Cash on Pickup | OR / Receipt No.: ${paymentReference}`,
                    },
                  ];

              return (
                <div className="admin-dialog__body admin-stack-md">
                  <div style={{ display: 'grid', gap: 4 }}>
                    <div className="admin-muted" style={{ fontSize: 12, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      Request
                    </div>
                    <div className="admin-row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: '1.02rem', color: '#0f172a' }}>{item.requestNo || 'N/A'}</strong>
                      <span className="admin-muted" style={{ fontSize: '0.9rem' }}>{item.memberName || item.member || 'Member'}</span>
                    </div>
                  </div>

                  <div
                    style={{
                      position: 'relative',
                      marginLeft: 6,
                      paddingLeft: 28,
                      display: 'grid',
                      gap: 16,
                    }}
                  >
                    <div
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        left: 7,
                        top: 4,
                        bottom: 4,
                        width: 2,
                        background: '#e2e8f0',
                      }}
                    />
                    {timelineRows.map((step) => {
                      const done = Boolean(step.when);
                      return (
                        <div
                          key={step.key}
                          style={{
                            position: 'relative',
                            opacity: done ? 1 : 0.45,
                            paddingBottom: 2,
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              position: 'absolute',
                              left: -28,
                              top: 4,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              background: done ? '#2563eb' : '#cbd5e1',
                              boxShadow: done ? '0 0 0 4px rgba(37,99,235,0.16)' : 'none',
                            }}
                          />
                          <div style={{ display: 'grid', gap: 4 }}>
                            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.96rem' }}>{step.title}</div>
                            <div className="admin-muted" style={{ fontSize: '0.82rem' }}>
                              {step.when ? formatTimelineDate(step.when) : 'Pending'}
                            </div>
                          </div>
                          <div className="admin-muted" style={{ marginTop: 4, fontSize: '0.85rem', whiteSpace: 'pre-line' }}>{step.detail}</div>
                          {step.link ? (
                            <div style={{ marginTop: 6 }}>
                              <a href={step.link} target="_blank" rel="noopener noreferrer" className="admin-link">
                                {step.linkLabel}
                              </a>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  {normalizeText(item.status) === 'rejected' && item.rejectionReason ? (
                    <div style={{
                      border: '1px solid #fca5a5',
                      borderRadius: '0.5rem',
                      padding: '0.9rem',
                      backgroundColor: '#fef2f2',
                      display: 'grid',
                      gap: '0.6rem',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <XCircle size={18} style={{ color: '#dc2626', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#991b1b' }}>Rejection Summary</span>
                      </div>
                      <div style={{ display: 'grid', gap: '0.35rem', paddingLeft: 26 }}>
                        <div>
                          <span style={{ color: '#991b1b', fontSize: '0.85rem', fontWeight: 600 }}>Reason: </span>
                          <span style={{ color: '#7f1d1d', fontSize: '0.85rem' }}>{item.rejectionReason}</span>
                        </div>
                        {item.rejectedBy ? (
                          <div>
                            <span style={{ color: '#991b1b', fontSize: '0.85rem', fontWeight: 600 }}>Rejected By: </span>
                            <span style={{ color: '#7f1d1d', fontSize: '0.85rem' }}>{item.rejectedBy}</span>
                          </div>
                        ) : null}
                        {item.rejectedAt ? (
                          <div>
                            <span style={{ color: '#991b1b', fontSize: '0.85rem', fontWeight: 600 }}>Rejected At: </span>
                            <span style={{ color: '#7f1d1d', fontSize: '0.85rem' }}>{formatTimelineDate(item.rejectedAt)}</span>
                          </div>
                        ) : null}
                        {item.refundId ? (
                          <>
                            <div>
                              <span style={{ color: '#991b1b', fontSize: '0.85rem', fontWeight: 600 }}>Refund Status: </span>
                              <span style={{
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                padding: '0.15rem 0.5rem',
                                borderRadius: '9999px',
                                ...(item.refundStatus === 'succeeded'
                                  ? { backgroundColor: '#d1fae5', color: '#065f46' }
                                  : item.refundStatus === 'failed'
                                    ? { backgroundColor: '#fee2e2', color: '#991b1b' }
                                    : { backgroundColor: '#dbeafe', color: '#1e40af' }),
                              }}>
                                {formatLabel(item.refundStatus || 'processing')}
                              </span>
                            </div>
                            <div>
                              <span style={{ color: '#991b1b', fontSize: '0.85rem', fontWeight: 600 }}>Stripe Refund ID: </span>
                              <span style={{ color: '#7f1d1d', fontSize: '0.82rem', fontFamily: 'monospace' }}>{item.refundId}</span>
                            </div>
                            {item.refundAmountPhp ? (
                              <div>
                                <span style={{ color: '#991b1b', fontSize: '0.85rem', fontWeight: 600 }}>Refund Amount: </span>
                                <span style={{ color: '#7f1d1d', fontSize: '0.85rem', fontWeight: 700 }}>{formatPeso(item.refundAmountPhp)}</span>
                              </div>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })()}
            <footer className="admin-dialog__footer admin-dialog__footer--grid">
              <button
                type="button"
                className="admin-button is-primary"
                onClick={closeDetailsModal}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {waybillModal.open ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeWaybillModal} role="presentation" />
          <div className="admin-dialog__panel admin-stack-md" role="document" style={{ maxWidth: 920 }}>
            <header className="admin-dialog__header">
              <h3>Waybill Preview {waybillModal.item?.requestNo ? `- ${waybillModal.item.requestNo}` : ''}</h3>
              <button type="button" className="admin-icon-button" onClick={closeWaybillModal} aria-label="Close">
                <X size={16} />
              </button>
            </header>
            <div className="admin-dialog__body admin-stack-md">
              {waybillModal.loading ? (
                <div className="admin-muted" style={{ padding: '1rem 0' }}>Loading waybill preview...</div>
              ) : null}
              {waybillModal.error ? (
                <div className="admin-alert is-error">{waybillModal.error}</div>
              ) : null}
              {!waybillModal.loading && !waybillModal.error && waybillModal.html ? (
                <iframe
                  ref={waybillFrameRef}
                  title="Waybill Preview"
                  srcDoc={waybillModal.html}
                  style={{ width: '100%', minHeight: 600, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}
                />
              ) : null}
            </div>
            <footer className="admin-dialog__footer admin-dialog__footer--grid">
              <button
                type="button"
                className="admin-button admin-button--ghost admin-dialog__footer-back"
                onClick={closeWaybillModal}
              >
                Close
              </button>
              <button
                type="button"
                className="admin-button is-primary"
                onClick={printWaybillFromModal}
                disabled={waybillModal.loading || !waybillModal.html || Boolean(waybillModal.error)}
              >
                <PrinterIcon size={14} /> Print Waybill
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {releaseModal.open ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeReleaseModal} role="presentation" />
          <div className="admin-dialog__panel admin-stack-md" role="document">
            <header className="admin-dialog__header">
              <h3>{releaseModal.mode === 'shipping' ? 'Update Shipping Status' : 'Release & Record Cash'}</h3>
              <button type="button" className="admin-icon-button" onClick={closeReleaseModal} aria-label="Close">
                <X size={16} />
              </button>
            </header>
            <form className="admin-dialog__body admin-stack-md" onSubmit={handleReleaseSubmit}>
              <div className="admin-field">
                <label>Request</label>
                <div className="admin-muted">
                  {releaseModal.item?.requestNo} — {releaseModal.item?.memberName || releaseModal.item?.member || 'Member'}
                </div>
              </div>
              {releaseModal.mode === 'shipping' ? (
                <>
                  <div className="admin-field">
                    <label>Auto OR / Stripe Reference</label>
                    <div className="admin-muted" style={{ wordBreak: 'break-all' }}>
                      {releaseModal.paymentReference || 'Not available'}
                    </div>
                  </div>
                  <div className="admin-field">
                    <label htmlFor="shippingStatus">Shipping status</label>
                    <select
                      id="shippingStatus"
                      className="admin-input"
                      value={releaseModal.shippingStatus}
                      onChange={(e) => setReleaseModal((s) => ({ ...s, shippingStatus: e.target.value }))}
                    >
                      <option value="preparing">Preparing</option>
                      <option value="shipped">Shipping / Shipped</option>
                      <option value="delivered">Delivered</option>
                    </select>
                  </div>
                  <div className="admin-field">
                    <label htmlFor="trackingNumber">Tracking number</label>
                    <input
                      id="trackingNumber"
                      className="admin-input"
                      value={releaseModal.trackingNumber}
                      onChange={(e) => setReleaseModal((s) => ({ ...s, trackingNumber: e.target.value }))}
                      placeholder="Required when status is Shipping/Shipped"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="admin-field">
                    <label htmlFor="paymentReference">OR / receipt number</label>
                    <input
                      id="paymentReference"
                      className="admin-input"
                      value={releaseModal.paymentReference}
                      onChange={(e) => setReleaseModal((s) => ({ ...s, paymentReference: e.target.value }))}
                      placeholder="Required"
                      required
                    />
                    {releaseRefMismatch && releaseModal.paymentReference ? (
                      <div className="admin-alert is-error id-card-queue__inline-alert">
                        Entered code does not match this request.
                      </div>
                    ) : null}
                  </div>
                  <div className="admin-field">
                    <label htmlFor="paymentNotes">Pickup notes</label>
                    <textarea
                      id="paymentNotes"
                      className="admin-input"
                      rows={3}
                      value={releaseModal.paymentNotes}
                      onChange={(e) => setReleaseModal((s) => ({ ...s, paymentNotes: e.target.value }))}
                      placeholder="Notes for cashier or member"
                    />
                  </div>
                </>
              )}
            </form>
            <footer className="admin-dialog__footer admin-dialog__footer--grid">
              <button
                type="button"
                className="admin-button admin-button--ghost admin-dialog__footer-back"
                onClick={closeReleaseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-button is-primary"
                onClick={handleReleaseSubmit}
                disabled={
                  actionLoadingId === releaseModal.item?.id ||
                  (
                    releaseModal.mode === 'cash'
                      ? !(releaseModal.paymentReference || "").trim()
                      : (normalizeText(releaseModal.shippingStatus) === 'shipped' && (releaseModal.trackingNumber || '').trim().length < 3)
                  )
                }
              >
                {actionLoadingId === releaseModal.item?.id ? <Loader2 size={14} className="admin-spin" /> : null}
                {releaseModal.mode === 'shipping' ? 'Save Shipping Update' : 'Record Cash & Release'}
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {rejectModal.open ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeRejectModal} role="presentation" />
          <div className="admin-dialog__panel admin-stack-md" role="document">
            <header className="admin-dialog__header">
              <h3>Reject ID Card Request</h3>
              <button type="button" className="admin-icon-button" onClick={closeRejectModal} aria-label="Close">
                <X size={16} />
              </button>
            </header>
            <div className="admin-dialog__body admin-stack-md">
              <div className="admin-field">
                <label>Request</label>
                <div className="admin-muted">
                  {rejectModal.item?.requestNo} — {rejectModal.item?.memberName || rejectModal.item?.member || 'Member'}
                </div>
              </div>
              {rejectModal.item?.paymentAmountPhp && normalizeText(rejectModal.item?.paymentMethod) === 'stripe' && normalizeText(rejectModal.item?.paymentStatus) === 'paid' ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #fbbf24',
                    backgroundColor: '#fffbeb',
                  }}
                >
                  <AlertTriangle size={18} style={{ color: '#d97706', flexShrink: 0, marginTop: 2 }} />
                  <span style={{ fontSize: '0.88rem', color: '#92400e', lineHeight: 1.5 }}>
                    Rejecting this request will automatically initiate a full refund of{' '}
                    <strong>{formatPeso(rejectModal.item.paymentAmountPhp)}</strong> via Stripe.
                  </span>
                </div>
              ) : null}
              <div className="admin-field">
                <label htmlFor="rejectionReason">Rejection Reason <span style={{ color: '#dc2626' }}>*</span></label>
                <textarea
                  id="rejectionReason"
                  className="admin-input"
                  rows={4}
                  value={rejectModal.reason}
                  onChange={(e) => setRejectModal((s) => ({ ...s, reason: e.target.value }))}
                  placeholder="e.g., Invalid Photo, Not a Union Member, Duplicate Request"
                  required
                />
              </div>
            </div>
            <footer className="admin-dialog__footer admin-dialog__footer--grid">
              <button
                type="button"
                className="admin-button admin-button--ghost admin-dialog__footer-back"
                onClick={closeRejectModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-button"
                style={{ backgroundColor: '#dc2626', color: '#fff', borderColor: '#dc2626' }}
                onClick={handleRejectSubmit}
                disabled={
                  actionLoadingId === rejectModal.item?.id ||
                  !(rejectModal.reason || '').trim()
                }
              >
                {actionLoadingId === rejectModal.item?.id ? <Loader2 size={14} className="admin-spin" /> : <XCircle size={14} />}
                Reject Request
              </button>
            </footer>
          </div>
        </div>
      ) : null}

    </div>
  );
}
